import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Person as PersonIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Quiz as QuizIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AppContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ClubService from '../services/clubService';
import { ClubApplication } from '../types';

interface ClubMember {
  id: string;
  name: string;
  email: string;
  role: string;
  branch: string;
  memberRole: string;
  joinedAt: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`club-management-tabpanel-${index}`}
      aria-labelledby={`club-management-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const ClubManagementPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [applications, setApplications] = useState<ClubApplication[]>([]);
  const [members, setMembers] = useState<ClubMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClub, setSelectedClub] = useState<string>('');
  const [userClubs, setUserClubs] = useState<any[]>([]);

  const [reviewDialog, setReviewDialog] = useState<{
    open: boolean;
    application: ClubApplication | null;
    action: 'approve' | 'reject';
  }>({ open: false, application: null, action: 'approve' });

  const [memberRoleDialog, setMemberRoleDialog] = useState<{
    open: boolean;
    member: ClubMember | null;
  }>({ open: false, member: null });

  const [feedback, setFeedback] = useState('');
  const [memberRole, setMemberRole] = useState('Member');
  const [newMemberRole, setNewMemberRole] = useState('Member');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (user?.clubs && Array.isArray(user.clubs)) {
      // Filter clubs where user is a mentor or has management rights
      const managedClubs = user.clubs.filter((club: any) =>
        club && (
          club.memberRole === 'President' ||
          club.memberRole === 'Vice President' ||
          user.role === 'Faculty'
        )
      );
      setUserClubs(managedClubs);
      if (managedClubs.length > 0) {
        setSelectedClub(managedClubs[0].id);
      }
    } else {
      setUserClubs([]);
    }
  }, [user]);

  useEffect(() => {
    if (selectedClub) {
      loadClubData();
    }
  }, [selectedClub]);

  const loadClubData = async () => {
    if (!selectedClub) return;

    try {
      setLoading(true);
      setError(null);

      const [applicationsData, membersData] = await Promise.all([
        ClubService.getClubApplications(selectedClub),
        ClubService.getClubMembers(selectedClub),
      ]);

      setApplications(applicationsData);
      setMembers(membersData);
    } catch (error: any) {
      console.error('Failed to load club data:', error);
      setError('Failed to load club data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewApplication = async () => {
    if (!reviewDialog.application || !selectedClub) return;

    try {
      setActionLoading(true);
      await ClubService.reviewApplication(
        selectedClub,
        reviewDialog.application.id,
        reviewDialog.action === 'approve' ? 'approved' : 'rejected',
        reviewDialog.action === 'approve' ? memberRole : undefined,
        feedback
      );

      setReviewDialog({ open: false, application: null, action: 'approve' });
      setFeedback('');
      setMemberRole('Member');
      loadClubData(); // Refresh data
    } catch (error: any) {
      console.error('Failed to review application:', error);
      setError('Failed to review application. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateMemberRole = async () => {
    if (!memberRoleDialog.member || !selectedClub) return;

    try {
      setActionLoading(true);
      await ClubService.updateMemberRole(selectedClub, memberRoleDialog.member.id, newMemberRole);

      setMemberRoleDialog({ open: false, member: null });
      setNewMemberRole('Member');
      loadClubData(); // Refresh data
    } catch (error: any) {
      console.error('Failed to update member role:', error);
      setError('Failed to update member role. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!selectedClub || !window.confirm('Are you sure you want to remove this member?')) return;

    try {
      setActionLoading(true);
      await ClubService.removeMember(selectedClub, memberId);
      loadClubData(); // Refresh data
    } catch (error: any) {
      console.error('Failed to remove member:', error);
      setError('Failed to remove member. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'pending':
        return <Chip label="Pending" color="warning" size="small" />;
      case 'approved':
        return <Chip label="Approved" color="success" size="small" />;
      case 'rejected':
        return <Chip label="Rejected" color="error" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'President': return 'error';
      case 'Vice President': return 'warning';
      case 'Secretary': return 'info';
      case 'Treasurer': return 'secondary';
      default: return 'primary';
    }
  };

  if (userClubs.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <CardContent>
            <SettingsIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 3 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              No Clubs to Manage
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              You don't have management rights for any clubs. You need to be a club officer or faculty mentor to access this page.
            </Typography>
            <Button variant="contained" href="/clubs">
              Browse Clubs
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
            Club Management
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Manage applications and members for your clubs
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadClubData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Club Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl fullWidth>
            <InputLabel>Select Club</InputLabel>
            <Select
              value={selectedClub}
              onChange={(e) => setSelectedClub(e.target.value)}
              label="Select Club"
            >
              {userClubs.map((club) => (
                <MenuItem key={club.id} value={club.id}>
                  {club.name} ({club.memberRole})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {selectedClub && (
        <>
          {/* Statistics Cards */}
          <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
            <Card sx={{ flex: 1 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="warning.main" sx={{ fontWeight: 700 }}>
                  {Array.isArray(applications) ? applications.filter(app => app && app.status === 'pending').length : 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Applications
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="primary.main" sx={{ fontWeight: 700 }}>
                  {members.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Members
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="success.main" sx={{ fontWeight: 700 }}>
                  {Array.isArray(applications) ? applications.filter(app => app && app.status === 'approved').length : 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Approved This Month
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Tabs */}
          <Card sx={{ mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              aria-label="club management tabs"
              variant="fullWidth"
            >
              <Tab label={`Applications (${Array.isArray(applications) ? applications.filter(app => app && app.status === 'pending').length : 0})`} />
              <Tab label={`Members (${Array.isArray(members) ? members.length : 0})`} />
            </Tabs>
          </Card>

          {loading ? (
            <LoadingSpinner message="Loading club data..." />
          ) : (
            <>
              {/* Applications Tab */}
              <TabPanel value={activeTab} index={0}>
                {!Array.isArray(applications) || applications.filter(app => app && app.status === 'pending').length === 0 ? (
                  <Card sx={{ textAlign: 'center', py: 8 }}>
                    <CardContent>
                      <QuizIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 3 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        No Pending Applications
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        All applications have been reviewed.
                      </Typography>
                    </CardContent>
                  </Card>
                ) : (
                  <List>
                    {Array.isArray(applications) && applications.filter(app => app && app.status === 'pending').map((application) => (
                      <Card key={application.id} sx={{ mb: 2 }}>
                        <ListItem sx={{ p: 3 }}>
                          <ListItemAvatar>
                            <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                              {application.applicant.name.charAt(0)}
                            </Avatar>
                          </ListItemAvatar>

                          <ListItemText
                            sx={{ ml: 2 }}
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {application.applicant.name}
                                </Typography>
                                {getStatusChip(application.status)}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  Email: {application.applicant.email}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  Branch: {application.applicant.branch}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  Quiz Score: {application.score}% ({application.score} out of {application.answers.length} correct)
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Applied: {formatDate(application.submittedAt)}
                                </Typography>
                              </Box>
                            }
                          />

                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="contained"
                              color="success"
                              startIcon={<ApproveIcon />}
                              onClick={() => setReviewDialog({ open: true, application, action: 'approve' })}
                              size="small"
                            >
                              Approve
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<RejectIcon />}
                              onClick={() => setReviewDialog({ open: true, application, action: 'reject' })}
                              size="small"
                            >
                              Reject
                            </Button>
                          </Box>
                        </ListItem>
                      </Card>
                    ))}
                  </List>
                )}
              </TabPanel>

              {/* Members Tab */}
              <TabPanel value={activeTab} index={1}>
                {members.length === 0 ? (
                  <Card sx={{ textAlign: 'center', py: 8 }}>
                    <CardContent>
                      <PersonIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 3 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        No Members Yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Approve some applications to see members here.
                      </Typography>
                    </CardContent>
                  </Card>
                ) : (
                  <List>
                    {members.map((member) => (
                      <Card key={member.id} sx={{ mb: 2 }}>
                        <ListItem sx={{ p: 3 }}>
                          <ListItemAvatar>
                            <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                              {member.name.charAt(0)}
                            </Avatar>
                          </ListItemAvatar>

                          <ListItemText
                            sx={{ ml: 2 }}
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {member.name}
                                </Typography>
                                <Chip
                                  label={member.memberRole}
                                  color={getRoleColor(member.memberRole) as any}
                                  size="small"
                                />
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  Email: {member.email}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  Branch: {member.branch}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Joined: {formatDate(member.joinedAt)}
                                </Typography>
                              </Box>
                            }
                          />

                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setMemberRoleDialog({ open: true, member })}
                            >
                              Change Role
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => handleRemoveMember(member.id)}
                            >
                              Remove
                            </Button>
                          </Box>
                        </ListItem>
                      </Card>
                    ))}
                  </List>
                )}
              </TabPanel>
            </>
          )}
        </>
      )}

      {/* Review Application Dialog */}
      <Dialog
        open={reviewDialog.open}
        onClose={() => setReviewDialog({ open: false, application: null, action: 'approve' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {reviewDialog.action === 'approve' ? 'Approve Application' : 'Reject Application'}
        </DialogTitle>
        <DialogContent>
          {reviewDialog.application && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {reviewDialog.action === 'approve'
                  ? `Approve ${reviewDialog.application.applicant.name}'s application?`
                  : `Reject ${reviewDialog.application.applicant.name}'s application?`}
              </Typography>

              {reviewDialog.action === 'approve' && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Member Role</InputLabel>
                  <Select
                    value={memberRole}
                    onChange={(e) => setMemberRole(e.target.value)}
                    label="Member Role"
                  >
                    <MenuItem value="Member">Member</MenuItem>
                    <MenuItem value="Secretary">Secretary</MenuItem>
                    <MenuItem value="Treasurer">Treasurer</MenuItem>
                    <MenuItem value="Vice President">Vice President</MenuItem>
                  </Select>
                </FormControl>
              )}

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Feedback (Optional)"
                placeholder="Provide feedback to the applicant..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setReviewDialog({ open: false, application: null, action: 'approve' });
              setFeedback('');
              setMemberRole('Member');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={reviewDialog.action === 'approve' ? 'success' : 'error'}
            onClick={handleReviewApplication}
            disabled={actionLoading}
          >
            {actionLoading ? 'Processing...' : reviewDialog.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Member Role Dialog */}
      <Dialog
        open={memberRoleDialog.open}
        onClose={() => setMemberRoleDialog({ open: false, member: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Member Role</DialogTitle>
        <DialogContent>
          {memberRoleDialog.member && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Change role for {memberRoleDialog.member.name}
              </Typography>
              <FormControl fullWidth>
                <InputLabel>New Role</InputLabel>
                <Select
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole(e.target.value)}
                  label="New Role"
                >
                  <MenuItem value="Member">Member</MenuItem>
                  <MenuItem value="Secretary">Secretary</MenuItem>
                  <MenuItem value="Treasurer">Treasurer</MenuItem>
                  <MenuItem value="Vice President">Vice President</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setMemberRoleDialog({ open: false, member: null });
              setNewMemberRole('Member');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateMemberRole}
            disabled={actionLoading}
          >
            {actionLoading ? 'Updating...' : 'Update Role'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClubManagementPage;